<template>
  <div class="container">
    <Button class="p-button-info p-button-lg my-4 mx-2 noprint" @click="print">
      <icon class="mr-3" :icon="['fas', 'print']" /> Print Performance
      Evaluation Report
    </Button>

    <div id="perf-eval-report">
      <PerfEvalCoverPage
        :sizing="sizing"
        :scenario="scenario"
        :perf-eval="perfeval"
        :date-generated="dateGenerated"
      />

      <section>
        <ReportSectionHeader
          section-title="Design Schematic"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <ScenarioEquipmentSchematic
          :scenario="scenario"
          :scenario-calculations="calcs"
          :sagd-mode="false"
          class="schematic"
        />
      </section>
      <section>
        <ReportSectionHeader
          section-title="Multi Frequency Head"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <PumpTornadoHeadChart
          draw-legend
          is-horizontal
          is-static
          :title="scenario?.title || ''"
          :show-operating-frequency="false"
          :min-frequency="frequencyRange[0]"
          :max-frequency="frequencyRange[1]"
          :pump-tornado-chart-data="tornadoChartData"
          :minimum-height-px="300"
          :aspect-ratio="aspectRatio"
          x-legend-label="In Situ Flow Rate"
          y-legend-label="TDH"
        />
        <h3>
          Head Curve Analysis
          {{ scenario?.title }}
        </h3>
        <StartUpHeadReport
          v-if="scenarioMap.scenario"
          :min-frequency="frequencyRange[0]"
          :max-frequency="frequencyRange[1]"
          :pump-tornado-chart-data="tornadoChartData"
          :scenario="scenarioMap.scenario"
        />
      </section>
      <section>
        <ReportSectionHeader
          section-title="Multi Frequency BHP"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <PumpTornadoBHPChart
          :aspect-ratio="aspectRatio"
          draw-legend
          is-horizontal
          is-static
          :title="scenario?.title || ''"
          :show-operating-frequency="false"
          :min-frequency="frequencyRange[0]"
          :max-frequency="frequencyRange[1]"
          :pump-tornado-chart-data="tornadoChartData"
          :scenario-calculations="calcs"
          :parameters="parameters"
          x-legend-label="In Situ Flow Rate"
          y-legend-label="Req BHP"
        />
        <h3>
          BHP Curve Analysis
          {{ scenario?.title }}
        </h3>
        <StartUpBhpReport
          class="mb-4"
          :min-frequency="frequencyRange[0]"
          :max-frequency="frequencyRange[1]"
          :pump-tornado-chart-data="tornadoChartData"
          :scenario="scenarioMap.scenario"
        />
      </section>
      <section>
        <ReportSectionHeader
          section-title="Motor Performance"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <MotorPerformanceChart
          :key="`mp-${scenario?.id}`"
          class="break-inside-avoid"
          :aspect-ratio="aspectRatio"
          draw-legend
          draw-motor-details
          is-static
          is-horizontal
          :title="scenario?.title || ''"
          :equipment="equipment"
          :parameters="parameters"
          :scenario-calculations="calcs"
        />
      </section>
      <section>
        <ReportSectionHeader
          section-title="Cable Temperature"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <CableTemperatureChart
          :key="`ct-${scenario?.id}`"
          class="break-inside-avoid"
          :aspect-ratio="aspectRatio"
          draw-legend
          is-horizontal
          :is-static="true"
          :title="scenario?.title || ''"
          :scenario-calculations="calcs"
          :equipment="equipment"
          :cables="cables"
        />
      </section>
      <section>
        <ReportSectionHeader
          section-title="Gas Curve"
          :customer-name="sizing?.customer?.customer_name"
          :sizing-name="sizing?.title"
          :well-name="headerWellNameOrLocation"
          :date-generated="dateGenerated"
        />
        <GasCurveChart
          :key="`gc-${scenario?.id}`"
          class="break-inside-avoid"
          :aspect-ratio="aspectRatio"
          draw-legend
          is-static
          is-horizontal
          :display-pump-thresholds="false"
          :title="scenario?.title || ''"
          :scenario-calculations="calcs"
          :correlations="correlations"
        />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import PerfEvalCoverPage from "@/components/reports/PerfEvalCoverPage.vue";
import ReportSectionHeader from "@/components/reports/ReportSectionHeader.vue";
import CableTemperatureChart from "@/components/scenarios/charts/CableTemperatureChart.vue";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import MotorPerformanceChart from "@/components/scenarios/charts/MotorPerformanceChart.vue";
import PumpTornadoBHPChart from "@/components/scenarios/charts/PumpTornadoBHPChart.vue";
import PumpTornadoHeadChart from "@/components/scenarios/charts/PumpTornadoHeadChart.vue";
import StartUpBhpReport from "@/components/scenarios/charts/StartUpBhpReport.vue";
import StartUpHeadReport from "@/components/scenarios/charts/StartUpHeadReport.vue";
import ScenarioEquipmentSchematic from "@/components/schematic/ScenarioEquipmentSchematic.vue";
import type { ScenarioMap } from "@/components/types";
import {
  cablesMapKey,
  correlationsKey,
  deviationSurveyKey,
  equipmentKey,
  housingsMapKey,
  intakesMapKey,
  parametersKey,
  perfevalFormKey,
  perfevalPumpsMapKey,
  scenarioCalculationsKey,
  scenarioKey,
  sealsMapKey,
  sizingKey,
  wellPropertiesKey,
  yToolBypassTubingsFormKey,
  yToolEquipmentTubingsFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { usePumpChartingData } from "@/libs/charting";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const dateGenerated = Intl.DateTimeFormat("en-us").format(new Date());

const props = defineProps<{
  frequencyRange: [number, number];
}>();

const router = useRouter();

// Add route query so copy and pasted links go directly to report page
// Needs to be child of PerformanceEvaluation rather than own route
// in order to inject custom perfeval calcs
onMounted(() => router.replace({ query: { report: "true" } }));
onBeforeUnmount(() => router.replace({ query: {} }));

// Custom to this perfeval
const perfeval = injectStrict(perfevalFormKey);
const pumps = injectStrict(perfevalPumpsMapKey);
const calcs = injectStrict(scenarioCalculationsKey);

// generic from scenario/sizing
const equipment = injectStrict(equipmentKey);
const parameters = injectStrict(parametersKey);
const wellProperties = injectStrict(wellPropertiesKey);

const sizing = injectStrict(sizingKey);
const scenario = injectStrict(scenarioKey);

// The head and BHP charts and both startup tables all show the same tornado
// data. Compute it once here; each of them would otherwise compute its own.
const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(scenario, sizing);
const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);
const tornadoChartData = computed(() =>
  usePumpChartingData(
    props.frequencyRange[0],
    props.frequencyRange[1],
    calcs,
    targetLiquidFlowUnit.value,
    targetPowerUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value
  )
);

const headerWellNameOrLocation = computed(() =>
  sizing.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps
    ? sizing.value?.hps_location ?? null
    : sizing.value?.well?.well_name
);

const correlations = injectStrict(correlationsKey);
const seals = injectStrict(sealsMapKey);
const intakes = injectStrict(intakesMapKey);
const housings = injectStrict(housingsMapKey);
const cables = injectStrict(cablesMapKey);
const deviationSurvey = injectStrict(deviationSurveyKey);
const yToolBypassTubingsForm = injectStrict(yToolBypassTubingsFormKey);
const yToolEquipmentTubingsForm = injectStrict(yToolEquipmentTubingsFormKey);

// Use generic y-tool forms directly
const yToolBypassTubings = computed(() => yToolBypassTubingsForm.data.value);
const yToolEquipmentTubings = computed(
  () => yToolEquipmentTubingsForm.data.value
);

const scenarioMap = computed<ScenarioMap[string]>(() => ({
  calcs,
  scenario: scenario.value,
  equipment: equipment.value,
  wellProperties: wellProperties.value,
  parameters: parameters.value,
  correlations: correlations.value,
  pumps: pumps.value,
  seals: seals.value,
  intakes: intakes.value,
  housings: housings.value,
  cables: cables.value,
  deviationSurvey: deviationSurvey.value,
  yToolBypassTubings: yToolBypassTubings.value,
  yToolEquipmentTubings: yToolEquipmentTubings.value
}));

const aspectRatio = ref(2);

function print() {
  window.print();
}
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/theme-spyglass/components/report_tables.scss";

// Shrink margin for printed report
// enough to squeeze out filename and junk
@page {
  margin: 30px;
}

@media screen {
  section {
    margin-top: 80px;
    padding-top: 80px;
    border-top: 2px solid var(--gray-500);
  }
}
.container {
  max-width: 816px;
  margin: 0 auto;
}
#perf-eval-report {
  height: 100%;

  section {
    page-break-before: always;
  }
}
</style>
